/**
 * React glue for settlement results (#788).
 *
 *   useSettlements(address)  the wallet's settlement rows. Saved rows show at once; the rows themselves come from the user
 *                            session's fills (#1809 W8, account/sessionFills.ts), so this reads no fills of its own, and
 *                            a new Settlement fill shows as soon as the session has it. Its settledOutcome is read a
 *                            moment later: the WS only says "settled", the result itself comes over /info (store.ts).
 *                            Lookups start once the session's download has answered, so they queue behind it on the
 *                            shared pacer, and can take minutes on a first load: rows fill in as they arrive and are
 *                            saved as they go. A run carries on across a page change, stops once no page wants it (at
 *                            the next paced request, so no answer in flight is lost), and resumes on the next look.
 *                            Nothing runs on a timer unless a lookup is due again (store.ts, nextLookupIn).
 *   useSettledOutcome(id)    market-level answer for one outcome (market page banner).
 */
import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useHeldFills, useUserSession } from '../account/UserSession';
import { backgroundPacer } from '../hl/weightPacer';
import { hlQueryKey, useHlEnv, useInfoClient } from '../hl/react';
import { useOutcomeTemplates } from '../describe/useDescribe';
import { marketResult, settlementFillsOf, settlementResult, type MarketResult, type SettlementFill, type SettlementResult } from './parse';
import {
  CACHE_MAX_FILLS,
  emptyStore,
  fetchSettledOutcome,
  loadStore,
  nextLookupIn,
  refreshStore,
  saveStore,
  withFills,
  type SettlementStore,
} from './store';

/** REST can lag a settlement by over a second, and a null answer would then wait out its back-off. */
const AFTER_FILL_MS = 3_000;
export const SETTLED_WAIT_MS = 30_000;
export const SETTLED_POLL_MS = 5_000;

export interface SettlementsState {
  results: SettlementResult[];
  /** Older settlements may be missing: only a user's most recent fills are read (#894). */
  truncated: boolean;
  /** No rows at all yet, and the session's fills are on their way. */
  loading: boolean;
  /** Fills or lookups are on their way (cached rows may be shown meanwhile). */
  refreshing: boolean;
  error: string | null;
  refresh: () => void;
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function useSettlements(address: string | null | undefined): SettlementsState {
  const env = useHlEnv();
  const info = useInfoClient();
  const qc = useQueryClient();
  const templates = useOutcomeTemplates();
  const session = useUserSession();
  const user = address ? address.toLowerCase() : null;
  const key = hlQueryKey(env, 'settlements', user);

  const held = useHeldFills(user);
  const settled = useMemo(() => settlementFillsOf(held.fills), [held.fills]);
  const latest = useRef<{ settled: SettlementFill[]; rows: number; capped: boolean }>({ settled, rows: 0, capped: false });
  latest.current = { settled, rows: held.fills.length, capped: held.capped };

  const q = useQuery<SettlementStore>({
    queryKey: key,
    // Behind the session's downloads: they are spent from the same bucket and lookups can wait.
    enabled: user !== null && held.complete,
    // Does not read `signal`: reading it would let TanStack abort the run, and roll the rows back, the moment one page
    // unmounts, even when the next page uses the same query. keepGoing stops it instead.
    queryFn: async () => {
      const prev = qc.getQueryData<SettlementStore>(key) ?? loadStore(env, user!) ?? emptyStore();
      const keepGoing = () => (qc.getQueryCache().find({ queryKey: key, exact: true })?.getObserversCount() ?? 0) > 0;
      const { settled, rows, capped } = latest.current;
      const base = withFills(prev, settled, rows, capped);
      saveStore(env, user!, base);
      const next = await refreshStore(info, base, {
        pacer: backgroundPacer(info),
        keepGoing,
        onProgress: (s) => {
          saveStore(env, user!, s);
          if (keepGoing()) qc.setQueryData(key, s);
        },
      });
      saveStore(env, user!, next);
      return next;
    },
    // Cached rows show immediately; updatedAt 0 makes them stale so the lookups due start right away.
    initialData: () => (user ? (loadStore(env, user) ?? undefined) : undefined),
    initialDataUpdatedAt: 0,
    staleTime: (query) => (query.state.data?.incomplete ? 0 : 60_000),
    refetchInterval: (query) => (query.state.data ? nextLookupIn(query.state.data, Date.now()) : false),
    retry: false,
  });

  // cancelRefetch false: never restart a running pass, that would pay for it twice.
  const lookUp = () => void qc.invalidateQueries({ queryKey: key, exact: true }, { cancelRefetch: false });
  const refresh = () => {
    session?.fills.retry();
    lookUp();
  };

  const hasNewFills = () => {
    const known = new Set(qc.getQueryData<SettlementStore>(key)?.fills.map((f) => f.tid));
    return latest.current.settled.slice(0, CACHE_MAX_FILLS).some((f) => !known.has(f.tid));
  };
  // One pending pass per hook: a burst of fills is one pass.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lookUpSoon = () => {
    if (timer.current) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      // A pass already running may have started before these fills: check again after it instead of joining it.
      if (qc.isFetching({ queryKey: key, exact: true }) > 0) lookUpSoon();
      else if (hasNewFills()) lookUp();
    }, AFTER_FILL_MS);
  };
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    },
    [env, user],
  );
  useEffect(() => {
    if (held.complete && hasNewFills()) lookUpSoon();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- both read the latest values; only new fills start a pass
  }, [settled, held.complete]);

  const store = useMemo(
    () => withFills(q.data ?? emptyStore(), settled, held.fills.length, held.capped),
    [q.data, settled, held.fills.length, held.capped],
  );
  const results = useMemo(
    () => store.fills.map((f) => settlementResult(f, store.outcomes[String(f.outcome)]?.s ?? null, { templates })),
    [store, templates],
  );

  if (!user) return { results: [], truncated: false, loading: false, refreshing: false, error: null, refresh };
  return {
    results,
    truncated: store.truncated === true,
    loading: results.length === 0 && !held.complete && held.error === null,
    refreshing: !held.complete || q.isFetching,
    error: q.error ? errorText(q.error) : held.error,
    refresh,
  };
}

/**
 * settledOutcome for one id (null while it is live or unknown). Only fetched when `enabled`.
 * `settledAt`: when its outcomeSettled event came. The page turns at that moment and REST can lag an event by over a second,
 * so for SETTLED_WAIT_MS a null is "not yet" (`waiting`), read again every SETTLED_POLL_MS. No event, no waiting: one read.
 */
export function useSettledOutcome(outcome: number | null, enabled: boolean, settledAt?: number) {
  const env = useHlEnv();
  const info = useInfoClient();
  const templates = useOutcomeTemplates();
  const inWait = () => settledAt !== undefined && Date.now() - settledAt < SETTLED_WAIT_MS;
  const q = useQuery({
    queryKey: hlQueryKey(env, 'settledOutcome', outcome),
    enabled: enabled && outcome !== null,
    queryFn: ({ signal }) => fetchSettledOutcome(info, outcome!, { signal }),
    // An answer is final (#1809); null is not an answer.
    staleTime: (query) => (query.state.data ? Infinity : 60_000),
    refetchInterval: (query) => (!query.state.data && inWait() ? SETTLED_POLL_MS : false),
    retry: false,
  });
  const result = useMemo<MarketResult | null>(() => (q.data ? marketResult(q.data, { templates }) : null), [q.data, templates]);
  // Read isFetching on every render: the end of each poll has to render, or `waiting` would never end.
  const fetching = q.isFetching;
  return {
    result,
    isPending: q.isPending && q.fetchStatus !== 'idle',
    waiting: !q.data && (fetching || inWait()) && settledAt !== undefined,
    isError: q.isError,
    refetch: q.refetch,
  };
}
